import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Section } from '../components/fx/AccentContext';
import { MagneticCTA, PADX } from '../components/home/ui';
import usePageMeta from '../lib/usePageMeta';
import YouTubeEmbed from '../components/news/YouTubeEmbed';
import {
  PODCAST_CHANNEL_URL,
  episodeSummary,
  findEpisode,
  formatEpisodeDate,
  isoDuration,
  parseDescription,
  podcastEpisodes,
} from '../data/podcast';

const SITE = 'https://salt-2.com';

// 構造化データ（JSON-LD）。説明文の中の「<」で script が閉じないように逃がす
function JsonLd({ data }) {
  const json = JSON.stringify(data).replace(/</g, '\\u003c');
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
}

const LABEL = 'text-[11px] font-semibold uppercase tracking-label';

/**
 * SALT2 PODCAST の回ごとのページ（/news/podcast/<番号>）。2026-10-18。
 * ニュースの一覧では動画が何本も並ぶだけなので、回ごとに「動画が主役のページ」を作り、
 * YouTube の説明文（あらすじ・話したこと・出演者）を文章で載せる。検索と AI の回答に中身を読んでもらうため。
 * 新しい回は、週1回の自動の取り込み（scripts/update-podcast.mjs）で podcast.json に入ると、そのままページになる。
 */
export default function PodcastEpisode() {
  const { no } = useParams();
  const ep = findEpisode(no);
  const parsed = parseDescription(ep?.description);
  const path = ep ? ep.path : '/news';
  const summary = episodeSummary(parsed, ep?.title || '');

  usePageMeta({
    title: ep ? `${ep.no} ${ep.title} | SALT2 PODCAST | 株式会社SALT2` : 'ニュース | SALT2',
    description: summary.length > 120 ? `${summary.slice(0, 119)}…` : summary,
    path,
    // 共有したときは、その回の動画のサムネイル（YouTube の 1280×720）を出す
    image: ep ? `https://i.ytimg.com/vi/${ep.id}/maxresdefault.jpg` : undefined,
  });

  if (!ep) return <Navigate to="/news" replace />;

  const index = podcastEpisodes.indexOf(ep);
  const newer = podcastEpisodes[index - 1];
  const older = podcastEpisodes[index + 1];
  const date = formatEpisodeDate(ep.published);
  const url = `${SITE}${ep.path}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'VideoObject',
        '@id': `${url}#video`,
        name: `${ep.title}（SALT2 PODCAST ${ep.no}）`,
        description: summary || ep.title,
        thumbnailUrl: [
          `https://i.ytimg.com/vi/${ep.id}/maxresdefault.jpg`,
          `https://i.ytimg.com/vi/${ep.id}/hqdefault.jpg`,
        ],
        uploadDate: ep.published || undefined,
        duration: isoDuration(ep.seconds),
        embedUrl: `https://www.youtube.com/embed/${ep.id}`,
        url,
        sameAs: `https://www.youtube.com/watch?v=${ep.id}`,
        inLanguage: 'ja',
        publisher: { '@id': `${SITE}/#organization` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'ニュース', item: `${SITE}/news` },
          { '@type': 'ListItem', position: 3, name: `SALT2 PODCAST ${ep.no}`, item: url },
        ],
      },
    ],
  };

  return (
    <main className="bg-paper text-ink">
      <JsonLd data={jsonLd} />
      <Section index={0} accent="blue" counted={false} className={`${PADX} border-b border-line pb-16 pt-32 md:pb-24 md:pt-44`}>
        <nav aria-label="パンくずリスト" className="text-[12px] text-gray-500">
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <li>
              <Link to="/news" className="transition-colors hover:text-ink">
                ニュース
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/news#podcast" className="transition-colors hover:text-ink">
                SALT2 PODCAST
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page" className="text-ink">
              {ep.no}
            </li>
          </ol>
        </nav>

        <div className={`mt-10 flex flex-wrap items-baseline gap-x-4 gap-y-1 ${LABEL}`}>
          <span className="text-live">SALT2 PODCAST {ep.no}</span>
          {date && <span className="tnum text-gray-500">{date}</span>}
          {ep.duration && <span className="tnum text-gray-500">{ep.duration}</span>}
        </div>
        <h1 className="mt-4 max-w-[30em] text-[26px] font-black leading-[1.45] tracking-display-ja text-ink md:text-[40px]">
          {ep.title}
        </h1>

        <div className="mt-10 max-w-[1080px] md:mt-12">
          <YouTubeEmbed episode={ep} big />
        </div>

        <div className="mt-14 grid grid-cols-12 gap-y-12 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-7">
            <div className={`${LABEL} text-gray-500`}>あらすじ</div>
            <div className="mt-4 space-y-4">
              {parsed.lead.map((p) => (
                <p key={p} className="whitespace-pre-line text-[15px] leading-[2] text-gray-800 md:text-[16px]">
                  {p}
                </p>
              ))}
            </div>
          </div>
          <div className="col-span-12 space-y-10 lg:col-span-5">
            {parsed.sections.map((s) => (
              <div key={s.title}>
                <div className={`${LABEL} border-b border-line pb-3 text-gray-500`}>{s.title}</div>
                {s.items.length > 0 && (
                  <ul className="mt-3">
                    {s.items.map((item) => (
                      <li key={item} className="border-b border-line/60 py-2.5 text-[14px] leading-[1.7] text-gray-800">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {s.paragraphs.map((p) => (
                  <p key={p} className="mt-3 whitespace-pre-line text-[13.5px] leading-[1.9] text-gray-700">
                    {p}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>

        {parsed.credits.length > 0 && (
          <div className="mt-12 space-y-1 text-[11px] leading-relaxed text-gray-500">
            {parsed.credits.map((c) => (
              <p key={c} className="whitespace-pre-line break-all">
                {c}
              </p>
            ))}
          </div>
        )}
      </Section>

      <Section index={1} accent="blue" className={`${PADX} border-b border-line py-14 md:py-20`}>
        <nav aria-label="ほかの回" className="grid gap-6 md:grid-cols-2">
          {older ? (
            <Link to={older.path} className="group block border border-line p-6 transition-colors hover:bg-paper-2">
              <span className={`flex items-center gap-2 ${LABEL} text-gray-500`}>
                <ArrowLeft size={14} aria-hidden="true" />
                前の回 {older.no}
              </span>
              <span className="mt-2 block text-[15px] font-bold leading-snug tracking-display-ja text-ink">{older.title}</span>
            </Link>
          ) : (
            <span className="hidden md:block" />
          )}
          {newer && (
            <Link to={newer.path} className="group block border border-line p-6 text-right transition-colors hover:bg-paper-2">
              <span className={`flex items-center justify-end gap-2 ${LABEL} text-gray-500`}>
                次の回 {newer.no}
                <ArrowRight size={14} aria-hidden="true" />
              </span>
              <span className="mt-2 block text-[15px] font-bold leading-snug tracking-display-ja text-ink">{newer.title}</span>
            </Link>
          )}
        </nav>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <MagneticCTA to="/news" variant="outline" cursorLabel="NEWS">
            エピソードの一覧へ
          </MagneticCTA>
          <MagneticCTA href={PODCAST_CHANNEL_URL} variant="live" icon="up" cursorLabel="WATCH">
            YouTubeチャンネルへ
          </MagneticCTA>
        </div>
      </Section>
    </main>
  );
}
